import type { DesktopClientStatus, DesktopHubLinkState } from '@dob/domain';

/**
 * What the sync indicator says on an ordinary PC (M7-4): how its operations leave it right now.
 * "Not your hub" wins: it needs a person, and it must never look like a normal state.
 */
export type ClientLink = 'starting' | 'hub' | 'server' | 'alone' | 'not_your_hub';

export function clientLink(status: DesktopClientStatus): ClientLink {
  if (status.hub.state === 'not_your_hub') return 'not_your_hub';
  if (status.sync.state === 'starting') return 'starting';
  if (status.sync.state === 'online') return status.sync.via === 'hub' ? 'hub' : 'server';
  return 'alone';
}

/** The fallback line under a hub problem: where the operations go meanwhile. */
export function fallbackLine(status: DesktopClientStatus): 'server' | 'alone' | null {
  if (status.hub.state === 'connected' || status.hub.state === 'searching') return null;
  if (status.sync.state === 'online' && status.sync.via === 'server') return 'server';
  if (status.sync.state === 'offline') return 'alone';
  return null;
}

/** Hub states that come with an explanation line (key suffix `…Hint`). */
export function stateHasHint(state: DesktopHubLinkState): boolean {
  return (
    state === 'not_paired' ||
    state === 'not_found' ||
    state === 'not_your_hub' ||
    state === 'unreachable' ||
    state === 'refused'
  );
}

/** Pairing should be offered first when the PC cannot trust the hub it sees. */
export function pairingSuggested(status: DesktopClientStatus): boolean {
  return status.hub.state === 'not_paired' || status.hub.state === 'not_your_hub';
}

/** Error codes of the Connection page that have their own words (`client.err<CODE>`). */
export const CLIENT_ERROR_CODES = [
  'INVALID_ADDRESS',
  'INVALID_PAIRING_CODE',
  'PAIR_NO_HUB_FOUND',
  'PAIR_UNREACHABLE',
  'PAIR_NOT_A_HUB',
  'PAIRING_CLOSED',
  'PAIR_UNKNOWN_DEVICE',
  'PAIR_WRONG_CODE',
  'PAIR_OTHER_LOCATION',
  // O27 (M7-5): the change needs a signed-in branch manager or IT on this PC.
  'SIGN_IN_REQUIRED',
  'NOT_ALLOWED',
  'SERVER_UNREACHABLE',
] as const;
export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];

export function clientErrorCode(code: string): ClientErrorCode | null {
  return (CLIENT_ERROR_CODES as readonly string[]).includes(code)
    ? (code as ClientErrorCode)
    : null;
}
